import { api } from '@/modules/shared/lib/axios'
import type { ResponseAPI } from '@/modules/shared/types/response-api.type'
import type { Order } from '../types/order.type'

export type TPayloadToOrderFormSearchInputComponent = {
  payload: {
    filters: {
      filterInput: string
      filter: string
      apiParam?: string
    }[]
  }
}

export type OrderSearchResponse = {
  orders: Order[]
  totalRecords: number
  hasMore: boolean
}

class OrderSearchService {
  private readonly ITEMS_PER_PAGE = 25

  private readonly FILTER_ORDER = [
    'data_emissao',
    'c.razao_social_nome',
    'c.cnpjcpf',
    's.sigla',
    'id_pedido'
  ]

  private buildFilterUrl(payload: TPayloadToOrderFormSearchInputComponent): string {
    let url = 'pedido'
    const filtersMap = new Map<string, string>()

    for (const filter of payload.payload.filters) {
      if (filter.filterInput && filter.filterInput.trim() !== '') {
        const param = filter.apiParam || filter.filter
        filtersMap.set(param, filter.filterInput)
      }
    }

    for (const param of this.FILTER_ORDER) {
      if (filtersMap.has(param)) {
        const value = filtersMap.get(param)
        if (!value) {
          continue
        }

        if (param === 'data_emissao') {
          const [startDate, endDate] = value.split('/')
          url = `${url}/${param}/${startDate}/${endDate}`
        } else {
          url = `${url}/${param}/${value}`
        }
      }
    }

    return url
  }

  private hasActiveFilters(payload: TPayloadToOrderFormSearchInputComponent): boolean {
    return payload.payload.filters.some(filter =>
      filter.filterInput &&
      filter.filterInput.trim() !== '' &&
      filter.filterInput !== 'undefined'
    )
  }

  async getTotalRecords(payload?: TPayloadToOrderFormSearchInputComponent): Promise<number> {
    try {
      let url: string

      if (payload && this.hasActiveFilters(payload)) {
        url = this.buildFilterUrl(payload)
      } else {
        url = 'pedido/historico'
      }

      const response = await api.get<ResponseAPI>(url)

      if (response.data?.body) {
        const parsed = JSON.parse(response.data.body)

        if (parsed.records && typeof parsed.records === 'number') {
          return parsed.records
        }

				if (Array.isArray(parsed)) {
          return parsed.length
        }

				if (parsed && typeof parsed === 'object') {
          const possibleCounts = Object.values(parsed).filter(val => typeof val === 'number' && val > 0)
          if (possibleCounts.length > 0) {
            return Math.max(...(possibleCounts as number[]))
          }
          const possibleArrays = Object.values(parsed).filter(val => Array.isArray(val))
          if (possibleArrays.length > 0) {
            return (possibleArrays[0] as any[]).length
          }
        }
      }
      return 0
    } catch (error) {
      console.error('❌ Erro ao buscar total de registros de pedidos:', error)
      throw error
    }
  }

  async getOrdersPage(payload: TPayloadToOrderFormSearchInputComponent | undefined, page: number): Promise<Order[]> {
    try {
      let url: string
      const hasFilters = payload && this.hasActiveFilters(payload)

      if (hasFilters) {
        url = this.buildFilterUrl(payload)
      } else {
        url = 'pedido/historico'
      }

      const offset = (page - 1) * this.ITEMS_PER_PAGE
      const urlWithPagination = `${url}?offset=${offset}&limit=${this.ITEMS_PER_PAGE}`

      const response = await api.get<ResponseAPI>(urlWithPagination)

      if (response.data?.body) {
        const data = JSON.parse(response.data.body)

        if (Array.isArray(data)) {
          return data
        }
        if (data && typeof data === 'object') {
          const possibleArrays = Object.values(data).filter(val => Array.isArray(val))
          if (possibleArrays.length > 0) {
            return possibleArrays[0] as Order[]
          }
        }
        return []
      }
      return []
    } catch (error) {
      console.error('❌ Erro ao buscar pedidos:', error)
      throw error
    }
  }

  async searchInitialOrders(payload?: TPayloadToOrderFormSearchInputComponent): Promise<OrderSearchResponse> {
    const totalRecords = await this.getTotalRecords(payload)

    if (totalRecords === 0) {
      return { orders: [], totalRecords: 0, hasMore: false }
    }

    const orders = await this.getOrdersPage(payload, 1)
    const hasMore = orders.length === this.ITEMS_PER_PAGE && totalRecords > this.ITEMS_PER_PAGE

    return { orders, totalRecords, hasMore }
  }

  async loadMoreOrders(
    payload: TPayloadToOrderFormSearchInputComponent | undefined,
    page: number,
    currentTotal: number,
    totalRecords: number
  ): Promise<{ orders: Order[], hasMore: boolean }> {
    if (currentTotal >= totalRecords) {
      return { orders: [], hasMore: false }
    }

    const orders = await this.getOrdersPage(payload, page)
    const newTotal = currentTotal + orders.length
    const hasMore = newTotal < totalRecords

    return { orders, hasMore }
  }
}

export const orderSearchService = new OrderSearchService()